// Sopwith 3D - minimap.
//
// The original status bar carried a miniature of the whole world: the
// terrain silhouette with a dot for every plane, target, bomb and flock.
// This draws the same picture onto a small canvas for the 3D HUD, at the
// original's reduction (one column per WRLD_RSX world columns), plus a
// marker for the span the camera can see.

import { MAX_Y, SCR_MNSH } from '../sim/constants';
import type { Game } from '../sim/game';
import { fuselageColor } from '../render/framebuffer';

export interface MinimapDot {
  x: number;
  y: number;
  clr: number;
}

export interface MinimapData {
  width: number;
  height: number;
  /** Terrain silhouette height per column (reduced). */
  profile: Uint8Array;
  dots: MinimapDot[];
}

/** Reduction factors, exactly as the original computed them. */
export function minimapScale(maxX: number): { rsx: number; rsy: number } {
  return { rsx: ((maxX / 320) | 0) * 2 + 1, rsy: ((MAX_Y / SCR_MNSH) | 0) + 1 };
}

/** Pure: compute the reduced terrain and object dots for the current tick. */
export function minimapData(g: Game): MinimapData {
  const maxX = g.level.maxX;
  const { rsx, rsy } = minimapScale(maxX);
  const width = Math.ceil(maxX / rsx);
  const height = ((MAX_Y / rsy) | 0) + 1;
  const profile = new Uint8Array(width);
  let maxh = 0;
  let dx = 0;
  let col = 0;
  for (let x = 0; x < maxX; x++) {
    maxh = Math.max(maxh, g.ground[x]);
    if (++dx === rsx || x === maxX - 1) {
      profile[col++] = (maxh / rsy) | 0;
      dx = 0;
      maxh = 0;
    }
  }
  const dots: MinimapDot[] = [];
  for (let ob = g.world.objtop; ob; ob = ob.next) {
    const sym = ob.symbol;
    if (!ob.onmap || !sym || ob.x < 0 || ob.x >= maxX) continue;
    const mapx = ob.x + ((sym.w / 2) | 0);
    const groundy = g.ground[ob.x];
    const mapy = ob.y - sym.h <= groundy ? ob.y - sym.h + 8 : ob.y - ((sym.h / 2) | 0);
    const px = (mapx / rsx) | 0;
    const py = (mapy / rsy) | 0;
    if (py < height) {
      dots.push({ x: px, y: py, clr: ob.clr });
    }
  }
  return { width, height, profile, dots };
}

const PALETTE = ['#0a0e14', '#2ec4b6', '#d94f8a', '#f4efe3'];

export class Minimap {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly scale = 2;

  constructor(container: HTMLElement) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'hud-minimap';
    this.canvas.style.imageRendering = 'pixelated';
    container.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d')!;
  }

  /** Redraw; `viewLeft`/`viewRight` mark the camera's visible world span. */
  draw(g: Game, viewLeft: number, viewRight: number): void {
    const d = minimapData(g);
    const s = this.scale;
    const w = d.width * s;
    const h = (d.height + 1) * s;
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
      this.canvas.style.width = `${w}px`;
      this.canvas.style.height = `${h}px`;
    }
    const ctx = this.ctx;
    ctx.fillStyle = 'rgba(10, 14, 20, 0.7)';
    ctx.fillRect(0, 0, w, h);

    // Visible span.
    const { rsx } = minimapScale(g.level.maxX);
    ctx.fillStyle = 'rgba(244, 239, 227, 0.12)';
    const vl = Math.max(0, (viewLeft / rsx) | 0) * s;
    const vr = Math.min(d.width, Math.ceil(viewRight / rsx)) * s;
    ctx.fillRect(vl, 0, Math.max(s, vr - vl), h);

    // Terrain silhouette, y up.
    ctx.fillStyle = PALETTE[3];
    for (let x = 0; x < d.width; x++) {
      const gy = d.profile[x];
      ctx.fillRect(x * s, h - (gy + 1) * s, s, s);
    }
    ctx.fillStyle = '#3f8a2e';
    for (let x = 0; x < d.width; x++) {
      const gy = d.profile[x];
      if (gy > 0) ctx.fillRect(x * s, h - gy * s, s, gy * s);
    }

    // Objects.
    for (const dot of d.dots) {
      ctx.fillStyle = PALETTE[fuselageColor(dot.clr)];
      ctx.fillRect(dot.x * s, h - (dot.y + 1) * s, s, s);
    }
    // The player is drawn last and a touch bigger.
    const p = g.consoleplayer;
    const pd = d.dots.find((dot) => dot.clr === p.clr);
    if (pd) {
      ctx.fillStyle = PALETTE[fuselageColor(p.clr)];
      ctx.fillRect(pd.x * s - 1, h - (pd.y + 1) * s - 1, s + 2, s + 2);
    }
  }

  setVisible(v: boolean): void {
    this.canvas.hidden = !v;
  }
}
